/**
 * Segnalazioni — trigger discreto per studenti (spec §2 passo 1, §5).
 *
 * Gate interno: visibile SOLO se l'utente NON è teacher-or-above
 * (inclusi gli anonimi). Rivalutato sull'evento `tl4k-auth-updated`
 * (pattern AdminSessionToolbar).
 */
import { useCallback, useEffect, useState } from 'react';
import { LifeBuoy } from 'lucide-react';
import {
  authService,
  hasTeacherOrAboveAccess,
  type User as AuthUser,
} from '@tl4k/frontend-shared';
import { IssuePairingModal } from './IssuePairingModal';

export interface ReportIssueButtonProps {
  /** Nome dell'app (contesto segnalazione). */
  appName: string;
  /** Variante compatta (solo icona più piccola). */
  compact?: boolean;
}

/**
 * Pulsante discreto "Segnala un problema": icona LifeBuoy a bassa enfasi.
 * Apre IssuePairingModal (QR + attesa identificazione teacher + form).
 */
export function ReportIssueButton({ appName, compact = false }: ReportIssueButtonProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [hovered, setHovered] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (e) {
      // Nessun utente leggibile → il pulsante resta visibile (target studenti/anonimi).
      console.warn('[ReportIssueButton] lettura utente fallita:', e);
      setUser(null);
    } finally {
      setAuthChecked(true);
    }
  }, []);

  useEffect(() => {
    void checkAuth();
    const handleAuthUpdated = () => void checkAuth();
    window.addEventListener('tl4k-auth-updated', handleAuthUpdated);
    return () => window.removeEventListener('tl4k-auth-updated', handleAuthUpdated);
  }, [checkAuth]);

  // Gate: nascosto per teacher-or-above (insegnanti segnalano da /admin).
  if (authChecked && hasTeacherOrAboveAccess(user)) {
    return null;
  }

  const size = compact ? 28 : 34;
  const iconSize = compact ? 14 : 16;

  return (
    <>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        aria-label="Segnala un problema"
        title="Segnala un problema"
        style={{
          minWidth: size,
          minHeight: size,
          width: size,
          height: size,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          border: '1px solid var(--tl4k-neutral-300)',
          borderRadius: 'var(--tl4k-border-radius-md)',
          background: hovered ? 'var(--tl4k-neutral-100)' : 'transparent',
          color: 'var(--tl4k-neutral-600)',
          cursor: 'pointer',
          flexShrink: 0,
          // Bassa enfasi: discreto finché non riceve hover/focus
          opacity: hovered ? 1 : 0.6,
          transition: 'opacity var(--tl4k-transition-fast), background var(--tl4k-transition-fast)',
        }}
      >
        <LifeBuoy size={iconSize} aria-hidden />
      </button>

      {modalOpen && (
        <IssuePairingModal appName={appName} onClose={() => setModalOpen(false)} />
      )}
    </>
  );
}

export default ReportIssueButton;
